import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { Logo } from "./Logo";
import { NAV_LINKS, SITE } from "@/lib/site";
import { useSectionNav } from "@/lib/scroll";

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const goTo = useSectionNav();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const handle = (id) => {
    setOpen(false);
    goTo(id);
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled || open ? "bg-ink/80 backdrop-blur-xl border-b border-white/[0.08]" : "bg-transparent border-b border-transparent"
      }`}
      data-testid="navbar"
    >
      <nav className="container-x flex h-[72px] items-center justify-between" aria-label="Main navigation">
        <Link to="/" onClick={(e) => { if (window.location.pathname === "/") { e.preventDefault(); handle("home"); } }} aria-label={`${SITE.name} home`} data-testid="nav-logo">
          <Logo />
        </Link>

        <ul className="hidden lg:flex items-center gap-1">
          {NAV_LINKS.map((l) => (
            <li key={l.id}>
              <button
                onClick={() => handle(l.id)}
                className="px-3.5 py-2 text-sm font-medium text-slate-300 hover:text-white rounded-full hover:bg-white/[0.05] transition-colors duration-300"
                data-testid={`nav-link-${l.id}`}
              >
                {l.label}
              </button>
            </li>
          ))}
        </ul>

        <div className="flex items-center gap-2">
          <a href={SITE.phoneHref} className="hidden md:inline-flex btn-ghost !min-h-[44px] !px-4 text-sm" data-testid="nav-phone-link">
            <Phone className="h-4 w-4" aria-hidden="true" /> {SITE.phone}
          </a>
          <button onClick={() => handle("quote")} className="hidden sm:inline-flex btn-primary !min-h-[44px] !px-5 text-sm" data-testid="nav-quote-btn">
            Get a Quote
          </button>
          <button
            className="lg:hidden inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-white/[0.04] text-white"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-controls="mobile-menu"
            aria-label={open ? "Close menu" : "Open menu"}
            data-testid="nav-mobile-toggle"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            id="mobile-menu"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden border-t border-white/[0.08] bg-ink/95 backdrop-blur-xl"
            data-testid="nav-mobile-menu"
          >
            <ul className="container-x flex flex-col py-4 max-h-[calc(100vh-72px)] overflow-y-auto">
              {NAV_LINKS.map((l) => (
                <li key={l.id}>
                  <button
                    onClick={() => handle(l.id)}
                    className="w-full text-left px-3 py-3.5 text-base font-medium text-slate-200 hover:text-white rounded-xl hover:bg-white/[0.05]"
                    data-testid={`nav-mobile-link-${l.id}`}
                  >
                    {l.label}
                  </button>
                </li>
              ))}
              <li className="mt-3 flex flex-col gap-3 px-1 pb-2">
                <button onClick={() => handle("quote")} className="btn-primary w-full" data-testid="nav-mobile-quote-btn">Get a Quote</button>
                <a href={SITE.phoneHref} className="btn-secondary w-full" data-testid="nav-mobile-phone-link">
                  <Phone className="h-4 w-4" aria-hidden="true" /> Call {SITE.phone}
                </a>
              </li>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
